import { css, html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { ref } from 'lit/directives/ref.js';

import type { TemplateInstance } from '@vouchfor/canvas-video';
import type { MediaPlayerProps } from '@vouchfor/media-player';
import type { Environment } from '~/utils/env';

import { EventForwardController } from './controllers/event-forwarder';
import { FetcherController } from './controllers/fetcher';
import { TrackingController } from './controllers/tracking';
import { MediaPlayerProxy } from '~/mixins/media-player-proxy';

import '@vouchfor/media-player';

type PlayerEmbedProps = Pick<MediaPlayerProps, 'data' | 'aspectRatio' | 'preload' | 'autoplay' | 'controls'> & {
  env: Environment;
  apiKey: string;
  disableTracking?: boolean;
  trackingSource?: string;
  vouchId?: string;
  templateId?: string;
  // Index of the questions to include starting from 1
  questions?: number[];
};

@customElement('vouch-embed-player')
class PlayerEmbed extends MediaPlayerProxy(LitElement) {
  static styles = [
    css`
      :host {
        display: flex;
      }
    `
  ];

  @property({ type: Object }) data: PlayerEmbedProps['data'];
  @property({ type: String }) vouchId: PlayerEmbedProps['vouchId'];
  @property({ type: String }) templateId: PlayerEmbedProps['templateId'];
  @property({ type: Array }) questions: PlayerEmbedProps['questions'];

  @property({ type: String }) env: PlayerEmbedProps['env'] = 'prod';
  @property({ type: String }) apiKey: PlayerEmbedProps['apiKey'] = '';
  @property({ type: Boolean }) disableTracking: PlayerEmbedProps['disableTracking'] = false;
  @property({ type: String }) trackingSource: PlayerEmbedProps['trackingSource'] = 'embedded_player';

  @property({ type: Array }) controls: PlayerEmbedProps['controls'];
  @property({ type: String }) preload: PlayerEmbedProps['preload'] = 'auto';
  @property({ type: Boolean }) autoplay: PlayerEmbedProps['autoplay'] = false;
  @property({ type: Number }) aspectRatio: PlayerEmbedProps['aspectRatio'] = 0;

  private eventController = new EventForwardController(this, [
    'durationchange',
    'ended',
    'error',
    'loadeddata',
    'pause',
    'stalled',
    'play',
    'playing',
    'ratechange',
    'scenechange',
    'seeking',
    'seeked',
    'timeupdate',
    'volumechange',
    'waiting',

    'video:loadeddata',
    'video:seeking',
    'video:seeked',
    'video:play',
    'video:playing',
    'video:pause',
    'video:stalled',
    'video:timeupdate',
    'video:ended',
    'video:error'
  ]);
  private _fetcherController = new FetcherController(this);
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  private _trackingController = new TrackingController(this);

  @state() vouch: PlayerEmbedProps['data'];
  @state() template: TemplateInstance | undefined;

  get fetching() {
    return this._fetcherController.fetching;
  }

  private _renderStyles() {
    if (!this.aspectRatio) {
      return html`
        <style>
          :host {
            width: 100%;
            height: 100%;
          }
        </style>
      `;
    }

    if (typeof this.aspectRatio === 'number') {
      return html`
        <style>
          :host {
            aspect-ratio: ${this.aspectRatio};
          }
        </style>
      `;
    }

    return null;
  }

  render() {
    return html`
      ${this._renderStyles()}
      <vmp-media-player
        ${ref(this._mediaPlayerRef)}
        ${this.eventController.register()}
        ?autoplay=${this.autoplay}
        ?loading=${this.fetching}
        .data=${this.vouch}
        .template=${this.template}
        aspectRatio=${ifDefined(this.aspectRatio)}
        preload=${ifDefined(this.preload)}
        .controls=${this.controls}
      ></vmp-media-player>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vouch-embed-player': PlayerEmbed;
  }

  namespace JSX {
    interface IntrinsicElements {
      'vouch-embed-player': PlayerEmbed;
    }
  }
}

export { PlayerEmbed };
export type { PlayerEmbedProps };
